// pages/Agents/Workflows/WorkflowGuide.js
import React from "react";
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const sections = [
  {
    title: "What are Agent Workflows?",
    content: `Agent Workflows let you automate multi-step tasks. There are two modes:

- **Workflow Pipeline**: A deterministic sequence of steps you define. Each step executes a specific action (e.g., create a volunteer, send an email). Steps run in order with optional conditions and branching.

- **AI Agent**: You set a goal and select which actions the AI can use. The AI decides which actions to call and in what order to accomplish the goal.

A single workflow can have both modes configured. The **Active Mode** toggle determines which one runs when you execute the workflow.`,
  },
  {
    title: "Creating a Workflow Pipeline",
    content: `1. Click **New Workflow** and give it a name.
2. Open the **Workflow Pipeline** accordion.
3. Click **Add Step** to add your first action.
4. For each step:
   - Select an **Action** (e.g., volunteer.create, comm.send_email_gmail).
   - Fill in the **parameters** for that action.
   - Optionally add a **Condition** to control when the step runs.
   - Set **On Success / On Failure** branching to skip to specific steps.
5. Set the **Active Mode** to "Workflow Pipeline".
6. Click **Save Workflow**.`,
  },
  {
    title: "Creating an AI Agent",
    content: `1. Click **New Workflow** and give it a name.
2. Open the **AI Agent** accordion.
3. Write a clear **Agent Goal** describing what the agent should accomplish. Example: "Create a volunteer from the input data, assign them to event #5, and send them a welcome email."
4. Select which **Allowed Actions** the AI can use (or leave empty to allow all).
5. Set the **Active Mode** to "AI Agent".
6. Click **Save Workflow**.

The AI agent uses Gemini to decide which actions to call based on your goal. It will execute actions step-by-step and verify results before proceeding.`,
  },
  {
    title: "Switching Active Mode",
    content: `Each workflow can have both a pipeline configuration and an AI agent configuration. The **Active Mode** determines which one runs when you execute the workflow.

- In the **workflow list**, click the swap icon to quickly toggle between Pipeline and AI Agent mode.
- In the **edit page**, use the Active Mode toggle at the top.

This lets you compare pipeline vs. AI execution for the same workflow, or switch between manual control and AI autonomy as needed.`,
  },
  {
    title: "Triggers",
    content: `Workflows can be triggered in three ways:

- **Manual**: Click the play button in the workflow list, or call the API endpoint.
- **Scheduled**: Set an interval in seconds. The workflow runs automatically at that interval when status is ACTIVE.
- **Event-based**: Specify an event type (e.g., "volunteer.created"). The workflow runs automatically when that event is emitted.

For Scheduled and Event triggers, the workflow must have status **ACTIVE** to run.`,
  },
  {
    title: "Viewing Results",
    content: `Click the history icon on any workflow to see execution results. Two views are available:

- **Run History tab**: Shows each execution run with status, trigger type, duration, and timestamp. Expand a run to see individual step details (action, input, output, errors).

- **Console tab**: A timeline view showing all activity across runs with human-readable insight titles (e.g., "Created volunteer", "Sent email via Gmail"). Click any entry to see the full input/output data.`,
  },
  {
    title: "Template Syntax (Pipeline Mode)",
    content: `In Workflow Pipeline mode, you can reference the output of previous steps in action parameters using template syntax:

- \`{{prev_output.field_name}}\` — references a field from the previous step's output.
- Example: If Step 1 creates a volunteer and outputs \`{ "volunteer_id": 42 }\`, Step 2 can use \`{{prev_output.volunteer_id}}\` to reference that ID.

Templates are resolved at runtime before each step executes.`,
  },
];

export default function WorkflowGuide() {
  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Learn how to create and manage agent workflows with the guide below.
      </Alert>
      {sections.map((section, index) => (
        <Accordion key={index} disableGutters sx={{ "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              {index + 1}. {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-line" }}
              dangerouslySetInnerHTML={{
                __html: section.content
                  .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                  .replace(/`(.*?)`/g, '<code style="background:#f5f5f5;padding:2px 6px;border-radius:4px;font-size:12px">$1</code>'),
              }}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
